import * as XLSX from "xlsx";

const pct = (v, total) => (total > 0 ? `${((Number(v) / total) * 100).toFixed(1)}%` : "0%");
const safeName = (s) => String(s || "carbon-footprint").replace(/[\\/:*?[\]]/g, "-").slice(0, 90);

/**
 * Download the Financial-Year-view dashboard as an Excel workbook, reflecting
 * the current FY / Location filters. Sheets: Summary, KPIs, Monthly.
 * @param {object} args
 * @param {string} args.scopeTitle
 * @param {string} args.fyLabel
 * @param {string} [args.locationLabel]
 * @param {object} args.payload - scope-summary payload for the scope
 */
export const downloadFyViewExcel = ({ scopeTitle, fyLabel, locationLabel = "All Locations", payload }) => {
  if (!payload) return;
  const total = Number(payload.total_emission || 0);
  const categories = payload.categories || {};
  const catKeys = Object.keys(categories);
  const wb = XLSX.utils.book_new();

  const summary = [
    ["Scope", scopeTitle],
    ["Financial Year", fyLabel],
    ["Location", locationLabel],
    [],
    ["Total emission (kgCO₂e)", total],
    [],
    ["Category", "kgCO₂e", "% of total"],
    ...catKeys.map((k) => [k, Number(categories[k]), pct(categories[k], total)]),
  ];
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(summary), "Summary");

  const kpiRows = [
    ["KPI", "Emission (kgCO₂e)", "% of scope"],
    ...(payload.topKpis || []).map((k) => [k.title || `KPI ${k.questionId}`, Number(k.value), `${k.pct}%`]),
  ];
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(kpiRows), "KPIs");

  const monthlyRows = [
    ["Month", ...catKeys, "Total"],
    ...(payload.monthly || []).map((r) => {
      const vals = catKeys.map((k) => Number(r[k] || 0));
      return [r.month, ...vals, vals.reduce((s, v) => s + v, 0)];
    }),
  ];
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(monthlyRows), "Monthly");

  XLSX.writeFile(wb, `${safeName(scopeTitle)} - ${safeName(fyLabel)}.xlsx`);
};

/**
 * Download the two-group comparison as an Excel workbook. Sheets: Summary, Trend
 * (and By category when present).
 * @param {object} args
 * @param {string} args.scopeTitle
 * @param {string} args.compareByLabel
 * @param {{label:string,total:number}} args.g1
 * @param {{label:string,total:number}} args.g2
 * @param {object[]} args.trendData - rows { month, g1, g2 }
 * @param {object[]} [args.catData] - rows { category, g1, g2 }
 */
export const downloadComparisonExcel = ({ scopeTitle, compareByLabel, g1, g2, trendData = [], catData = [] }) => {
  const wb = XLSX.utils.book_new();

  const summary = [
    ["Scope", scopeTitle],
    ["Compared by", compareByLabel],
    [],
    ["Group", "Label", "Total (kgCO₂e)"],
    ["Group 1", g1.label, Number(g1.total || 0)],
    ["Group 2", g2.label, Number(g2.total || 0)],
  ];
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(summary), "Summary");

  const trend = [
    ["Month", g1.label, g2.label],
    ...trendData.map((r) => [r.month, Number(r.g1 || 0), Number(r.g2 || 0)]),
  ];
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(trend), "Trend");

  if (catData.length) {
    const cats = [
      ["Category", g1.label, g2.label],
      ...catData.map((r) => [r.category, Number(r.g1 || 0), Number(r.g2 || 0)]),
    ];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(cats), "By category");
  }

  XLSX.writeFile(wb, `${safeName(scopeTitle)} - comparison.xlsx`);
};

/**
 * Download the period comparison (bars = years or months) as an Excel workbook.
 * @param {object} args
 * @param {string} args.scopeTitle
 * @param {string} args.axisLabel - e.g. "Jul · by financial year"
 * @param {{name:string,value:number}[]} args.bars
 */
export const downloadPeriodExcel = ({ scopeTitle, axisLabel, bars = [] }) => {
  const wb = XLSX.utils.book_new();
  const rows = [
    ["Scope", scopeTitle],
    ["Comparison", axisLabel],
    [],
    ["Period", "Emission (kgCO₂e)"],
    ...bars.map((b) => [b.name, Number(b.value || 0)]),
  ];
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(rows), "Period");
  XLSX.writeFile(wb, `${safeName(scopeTitle)} - period.xlsx`);
};

/** Small shared button style for the download control. */
export const downloadBtnStyle = {
  display: "inline-flex", alignItems: "center", gap: 6,
  height: 34, padding: "0 14px", borderRadius: 8,
  border: "1.5px solid var(--accent, #3f88a5)", background: "var(--accent, #3f88a5)",
  color: "#fff", fontSize: 12.5, fontWeight: 700, cursor: "pointer",
};
